import { getAppConfig } from '@services/config';
import { cmsHttpService, httpService } from '@services/http';
import { CACHE_TAGS } from '@services/http/http';
import { logger } from '@services/loggers';

import { ApiBannerType, ApiCmsOfferType, ConfigType } from '@app-types/api';
import { ContentEditorData, ContentEditorItem } from '@app-types/cms';
import { ContentEditorOptionType, ContentEditorParentalControlResponseType } from '@app-types/product';

const appConfig = getAppConfig();

export const getCmsOffers = async (): Promise<ApiCmsOfferType[] | null> => {
    type ResponseType = {
        items: ApiCmsOfferType[];
    };

    const path = appConfig.mcommerceApi.url + '/mco_offres';
    try {
        const res = await httpService.get<ResponseType>(path, undefined, {
            ttl: parseInt(getAppConfig().cache.ttl.products),
            tags: [CACHE_TAGS.PRODUCTS],
        });
        return res.items;
    } catch (error) {
        logger.log('path: ', path);
        logger.log('error getCmsOffers: ', error);
        return null;
    }
};

export const getDownloadConfiguration = async (): Promise<ConfigType | undefined> => {
    type ResponseType = {
        items: ConfigType[];
    };
    try {
        const path = appConfig.mcommerceApi.url + '/mco_config';
        const res = await httpService.get<ResponseType>(path, undefined, {
            ttl: Number.parseInt(getAppConfig().cache.ttl.config, 10),
            tags: [CACHE_TAGS.CONFIG],
        });
        return res.items[0];
    } catch (error) {
        logger.log('error fetchMcoConfig: ', error);
        return undefined;
    }
};

export const getPromoBanners = async (): Promise<ApiBannerType[]> => {
    type ResponseType = {
        items: ApiBannerType[];
    };
    try {
        const path = appConfig.mcommerceApi.url + '/mco_bannieres';
        const result = await httpService.get<ResponseType>(path, undefined, {
            ttl: 50,
            tags: [CACHE_TAGS.CMS],
        });
        const promoBanners = result.items;

        return promoBanners
            .filter((item) => item.enabled)
            .sort((banner1, banner2) => banner1.displayOrder - banner2.displayOrder);
    } catch (error) {
        logger.log('error fetchMcoBanners: ', error);
        return [];
    }
};

export const getRocProducts = async (): Promise<ContentEditorItem[]> => {
    return cmsHttpService
        .get<ContentEditorData>(`${appConfig.rocApi.url}`, undefined, {
            ttl: Number.parseInt(getAppConfig().cache.ttl.config, 10),
            tags: [CACHE_TAGS.CMS, CACHE_TAGS.CONFIG],
        })
        .then((response) => removeNullValues(response.items))

        .catch((error) => {
            logger.error('[error faiServices]: ', error);
            return [] as ContentEditorItem[];
        });
};

export const getParentalOptionsData = async (): Promise<ContentEditorOptionType[]> => {
    return cmsHttpService
        .get<ContentEditorParentalControlResponseType>(
            `${appConfig.assets.palpatine.checkout}/checkout_parental_control`,
            undefined,
            {
                ttl: Number.parseInt(getAppConfig().cache.ttl.config, 10),
                tags: [CACHE_TAGS.CMS, CACHE_TAGS.CONFIG],
            },
        )
        .then((response) => response.items[0]?.parental_control_choice.items || [])
        .catch((error) => {
            logger.error('[error optionsDataServices]: ', error);
            return [] as ContentEditorOptionType[];
        });
};

const removeNullValues = (toClean: ContentEditorItem[]) => {
    toClean.forEach((item) => {
        Object.keys(item).forEach((key) => {
            if (item[key as keyof ContentEditorItem] === null) {
                delete item[key as keyof ContentEditorItem];
            }
        });
    });

    return toClean;
};
